import React, { useMemo, useCallback } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType,
  type Node,
  type Edge,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import dagre from 'dagre';
import PSIFlowStageNode, { type StageNodeData } from './PSIFlowStageNode';
import type { PSIFlowDetail, FlowStage } from '@/types/psiFlow';
import { useTranslation } from 'react-i18next';

const NODE_WIDTH = 240;
const NODE_HEIGHT = 160;

const nodeTypes = { stageNode: PSIFlowStageNode };

interface SalesFlowViewProps {
  flowDetail: PSIFlowDetail;
  onOpenDocument?: (type: string, id: string) => void;
  onNodeClick?: (stage: FlowStage, flowDetail: PSIFlowDetail) => void;
}

const getLayoutedElements = (nodes: Node[], edges: Edge[]) => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: 'LR', nodesep: 100, ranksep: 120 });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: NODE_WIDTH, height: NODE_HEIGHT });
  });
  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - NODE_WIDTH / 2,
        y: nodeWithPosition.y - NODE_HEIGHT / 2,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

const SalesFlowView: React.FC<SalesFlowViewProps> = ({
  flowDetail,
  onOpenDocument,
  onNodeClick,
}) => {
  const { t } = useTranslation('psi');

  const sq = flowDetail.sales_quotation;
  const so = flowDetail.sales_order;
  const invoices = flowDetail.invoices || [];
  const returns = flowDetail.returns || [];

  const hasSO = !!so;
  const isDelivered = so?.status === 'delivered' || so?.status === 'completed';
  const isShipped = so?.status === 'shipped' || isDelivered;
  const hasInvoice = invoices.length > 0;
  const latestInvoice = hasInvoice ? invoices[0] : null;
  const invoicePaid = invoices.some((inv) => inv.status === 'paid');
  const hasReturn = returns.length > 0;
  const latestReturn = hasReturn ? returns[0] : null;

  const isOverdueSO =
    so &&
    (so.status === 'confirmed' || so.status === 'processing') &&
    new Date(so.delivery_date) < new Date();

  const { nodes: initialNodes, edges: initialEdges } = useMemo(() => {
    const nodes: Node<StageNodeData>[] = [
      {
        id: 'quotation',
        type: 'stageNode',
        position: { x: 0, y: 0 },
        draggable: false,
        data: {
          stage: 'quotation',
          label: t('flow.stages.quotation', 'Quotation'),
          docNumber: sq?.sq_number,
          status: sq?.status,
          amount: sq?.subtotal,
          currency: sq?.currency,
          date: sq?.quotation_date ? new Date(sq.quotation_date).toLocaleDateString() : undefined,
          hasDocument: !!sq,
          isCompleted: sq?.status === 'accepted',
          onOpen: sq ? () => onOpenDocument?.('sq', sq.sq_id) : undefined,
          onClick: sq ? () => onNodeClick?.('quotation', flowDetail) : undefined,
        },
      },
      {
        id: 'sales_order',
        type: 'stageNode',
        position: { x: 0, y: 0 },
        draggable: false,
        data: {
          stage: 'sales_order',
          label: t('flow.stages.sales_order', 'Sales Order'),
          docNumber: so?.so_number,
          status: so?.status,
          amount: so?.grand_total,
          currency: so?.currency,
          date: so?.order_date ? new Date(so.order_date).toLocaleDateString() : undefined,
          hasDocument: hasSO,
          isCompleted: so?.status === 'completed',
          isOverdue: isOverdueSO,
          onOpen: so ? () => onOpenDocument?.('so', so.so_id) : undefined,
          onClick: hasSO ? () => onNodeClick?.('sales_order', flowDetail) : undefined,
        },
      },
      {
        id: 'delivery',
        type: 'stageNode',
        position: { x: 0, y: 0 },
        draggable: false,
        data: {
          stage: 'delivery',
          label: t('flow.stages.delivery', 'Delivery'),
          hasDocument: isShipped,
          isCompleted: isDelivered,
          status: isDelivered ? 'delivered' : isShipped ? 'shipped' : undefined,
          date: so?.delivery_date ? new Date(so.delivery_date).toLocaleDateString() : undefined,
          onClick: isShipped ? () => onNodeClick?.('delivery', flowDetail) : undefined,
        },
      },
      {
        id: 'inventory',
        type: 'stageNode',
        position: { x: 0, y: 0 },
        draggable: false,
        data: {
          stage: 'inventory',
          label: t('flow.stages.inventory', 'Inventory'),
          hasDocument: isDelivered,
          isCompleted: isDelivered,
          status: isDelivered ? 'completed' : undefined,
          onClick: isDelivered ? () => onNodeClick?.('inventory', flowDetail) : undefined,
        },
      },
      {
        id: 'return',
        type: 'stageNode',
        position: { x: 0, y: 0 },
        draggable: false,
        data: {
          stage: 'return',
          label: t('flow.stages.return', 'Return of Goods'),
          docNumber: latestReturn?.return_number,
          status: latestReturn?.status,
          amount: latestReturn?.total_amount,
          currency: latestReturn?.currency,
          hasDocument: hasReturn,
          isCompleted: latestReturn?.status === 'completed',
          isOptional: true,
          onOpen: latestReturn
            ? () => onOpenDocument?.('return', latestReturn.return_id)
            : undefined,
          onClick: hasReturn ? () => onNodeClick?.('return', flowDetail) : undefined,
        },
      },
      {
        id: 'invoice',
        type: 'stageNode',
        position: { x: 0, y: 0 },
        draggable: false,
        data: {
          stage: 'invoice',
          label: t('flow.stages.invoice', 'Invoice'),
          docNumber: latestInvoice?.invoice_number,
          status: latestInvoice?.status,
          amount: latestInvoice?.grand_total,
          currency: latestInvoice?.currency,
          date: latestInvoice?.due_date
            ? new Date(latestInvoice.due_date).toLocaleDateString()
            : undefined,
          hasDocument: hasInvoice,
          isCompleted: invoicePaid,
          isOverdue: latestInvoice?.status === 'overdue',
          onOpen: latestInvoice
            ? () => onOpenDocument?.('invoice', latestInvoice.invoice_id)
            : undefined,
          onClick: hasInvoice ? () => onNodeClick?.('invoice', flowDetail) : undefined,
        },
      },
    ];

    const edges: Edge[] = [
      {
        id: 'sq-so',
        source: 'quotation',
        target: 'sales_order',
        markerEnd: { type: MarkerType.ArrowClosed, width: 20, height: 20 },
        style: { strokeWidth: 2, stroke: hasSO ? '#22c55e' : '#94a3b8' },
        animated: !!sq && !hasSO,
      },
      {
        id: 'so-delivery',
        source: 'sales_order',
        target: 'delivery',
        markerEnd: { type: MarkerType.ArrowClosed, width: 20, height: 20 },
        style: { strokeWidth: 2, stroke: isShipped ? '#22c55e' : isOverdueSO ? '#ef4444' : hasSO ? '#3b82f6' : '#94a3b8' },
        animated: hasSO && !isShipped,
      },
      {
        id: 'delivery-inventory',
        source: 'delivery',
        target: 'inventory',
        markerEnd: { type: MarkerType.ArrowClosed, width: 20, height: 20 },
        style: { strokeWidth: 2, stroke: isDelivered ? '#22c55e' : isShipped ? '#3b82f6' : '#94a3b8' },
        animated: isShipped && !isDelivered,
      },
      {
        id: 'inventory-return',
        source: 'inventory',
        target: 'return',
        markerEnd: { type: MarkerType.ArrowClosed, width: 20, height: 20 },
        style: { strokeWidth: 2, stroke: hasReturn ? '#22c55e' : '#94a3b8', strokeDasharray: hasReturn ? undefined : '5 5' },
        animated: false,
      },
      {
        id: 'return-invoice',
        source: 'return',
        target: 'invoice',
        markerEnd: { type: MarkerType.ArrowClosed, width: 20, height: 20 },
        style: { strokeWidth: 2, stroke: invoicePaid ? '#22c55e' : hasInvoice ? '#3b82f6' : '#94a3b8' },
        animated: (isDelivered || hasReturn) && !invoicePaid,
      },
    ];

    return getLayoutedElements(nodes as Node[], edges);
  }, [sq, so, invoices, returns, t, onOpenDocument, onNodeClick, flowDetail, hasSO, isDelivered, isShipped, hasInvoice, latestInvoice, invoicePaid, hasReturn, latestReturn, isOverdueSO]);

  const [nodes, , onNodesChange] = useNodesState(initialNodes);
  const [edges, , onEdgesChange] = useEdgesState(initialEdges);

  const minimapNodeColor = useCallback((node: Node) => {
    const data = node.data as unknown as StageNodeData;
    if (data.isOverdue) return '#ef4444';
    if (data.isCompleted) return '#22c55e';
    if (data.hasDocument) return '#3b82f6';
    return '#9ca3af';
  }, []);

  return (
    <div className="h-[450px] bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        nodeTypes={nodeTypes}
        fitView
        fitViewOptions={{ padding: 0.3 }}
        minZoom={0.5}
        maxZoom={1.5}
        nodesDraggable={false}
        nodesConnectable={false}
      >
        <Background color="#94a3b8" gap={20} size={1} />
        <Controls className="dark:bg-gray-700 dark:[&>button]:bg-gray-600 dark:[&>button]:text-gray-200 dark:[&>button]:border-gray-500" />
        <MiniMap nodeColor={minimapNodeColor} className="dark:bg-gray-700" />
      </ReactFlow>
      <div className="absolute bottom-4 left-4 flex gap-3 text-xs bg-white/90 dark:bg-gray-800/90 rounded px-3 py-2 shadow">
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-green-500" /> Completed</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-blue-500" /> In Progress</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-500" /> Overdue</span>
        <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-gray-400" /> Pending</span>
      </div>
    </div>
  );
};

export default SalesFlowView;
